<template>
	<div>
		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("transactions") }}</template>
				<template slot="actions">
					<j-button
						v-can="'writeTransactions'"
						type="primary"
						@click="$modal.show('addTransaction')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add_transaction") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedtransactions"
					:sortable="false"
					:isLoading="isLoading || isPaymentsLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template v-if="column.field === 'actions'">
								<crm-icon
									v-if="!item.isPayment"
									v-can="'writeTransactions'"
									name="edit"
									color="c6"
									class="cu2"
									@click.native="handleUpdate(item)"
								/>
							</template>
							<template v-else>
								<template v-if="column.field === 'transactionNumber'">
									<router-link
										v-if="!item.isPayment"
										:to="
											`/${
												$route.params.tenantId
											}/transactions/detail/transaction/${item.id}`
										"
									>
										<span> {{ item[column.field] || "---" }}</span>
									</router-link>
									<span v-else> {{ item[column.field] || "---" }}</span>
								</template>
								<template v-else>
									{{ item[column.field] }}
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<core-modal name="addTransaction" :headline="$t('add_transaction')">
				<transaction-create
					:policyId="id"
					@cancel="$modal.hide('addTransaction')"
					@transaction:added="
						$modal.hide('addTransaction'), fetchTransactions()
					"
				/>
			</core-modal>

			<!-- Update Transaction -->
			<core-modal name="updateTransaction" :headline="$t('update_transaction')">
				<transaction-update
					:policyId="id"
					:transactionId="transactionToUpdateId"
					:initialPayload="transactionToUpdatePayload"
					@transaction:updated="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
					@cancel="$modal.hide('updateTransaction')"
				/>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CoreModal from "@/modules/core/components/CoreModal"
import TransactionCreate from "../TransactionCreate"
import TransactionUpdate from "../TransactionUpdate"
import dayjs from "dayjs"
import api from "../../api/"
import get from "lodash.get"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		TransactionCreate,
		TransactionUpdate
	},

	data: () => ({
		isLoading: false,
		transactions: [],

		// payments from the Payments service (Wonder etc). Merged into the
		// single transaction history below - admin users see one list regardless of provider.
		payments: [],
		isPaymentsLoading: false,

		// When user clicks on edit icon, we save this id here
		transactionToUpdateId: null,
		transactionToUpdatePayload: null
	}),

	computed: {
		// get policy id from router
		id() {
			return this.$route.params.id
		},

		header() {
			return [
				{ text: this.$t("debitNoteNumber"), field: "transactionNumber" },
				{ text: this.$t("when"), field: "dateTime" },
				{ text: this.$t("amount"), field: "formattedAmount" },
				{ text: this.$t("payment_method"), field: "method" },
				{ text: this.$t("status"), field: "localizedStatus" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},

		computedtransactions() {
			const fmt = this.$store.state.settings.dateTimeFormat

			// Legacy transactions (Transactions service). Deprecated source, but older
			// policies still have records here, so keep showing them.
			const transactionRows = (this.transactions || []).map(item => ({
				id: item.id,
				sortDate: item.dateTime,
				dateTime: item.dateTime ? dayjs(item.dateTime).format(fmt) : null,
				formattedAmount: item.formattedTransaction,
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				localizedStatus: item.status, // TODO: localized
				method: item.method,
				localizedMethod: item.method
					? this.$t(`payment_methods_enums.${item.method}`)
					: null,
				transactionNumber: item.transactionNumber,
				isPayment: false
			}))

			// payments from the Payments service (Wonder etc), mapped into
			// the same transaction row shape so they appear in this one history table.
			const paymentRows = (this.payments || []).map(item => {
				// Align the payment status vocabulary with the transaction one
				const status = item.status === "SUCCEEDED" ? "APPROVED" : item.status
				return {
					id: item.id,
					sortDate: item.effectiveDate,
					dateTime: item.effectiveDate
						? dayjs(item.effectiveDate).format(fmt)
						: null,
					formattedAmount:
						item.money && item.money.paymentAmount != null
							? `${item.money.paymentAmount} ${item.money.paymentCurrencyDesc ||
									""}`.trim()
							: "---",
					status,
					localizedStatus: status,
					method: item.paymentMethod ? item.paymentMethod.toUpperCase() : "---",
					// Online payments have no debit note number.
					transactionNumber: null,
					isPayment: true
				}
			})

			// One unified list, most recent first.
			return [...transactionRows, ...paymentRows].sort((a, b) => {
				const dateA = a.sortDate || ""
				const dateB = b.sortDate || ""
				if (dateA < dateB) return 1
				if (dateA > dateB) return -1
				return 0
			})
		}
	},

	methods: {
		async fetchTransactions() {
			this.isLoading = true
			const res = await api.policyTransactions({
				where: { id: this.id }
			})
			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.transactions = get(res, "data.policies.list[0].transactions", [])
		},

		// fetch this policy's payments from the Payments service (V2).
		async fetchPayments() {
			this.isPaymentsLoading = true
			try {
				const res = await api.policyPayments({
					policyId: this.id,
					tenantId: this.$route.params.tenantId
				})

				if (res && res.errors) {
					this.$store.dispatch("showError", res.errors)
					return
				}

				this.payments = get(res, "data.payments.items", [])
			} catch (error) {
				this.$store.dispatch("showError", error)
			} finally {
				this.isPaymentsLoading = false
			}
		},

		handleUpdate(item) {
			this.transactionToUpdateId = item.id
			this.transactionToUpdatePayload = {
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				dateTime: item.dateTime,
				method: item.method
			}

			// Open popup
			this.$modal.show("updateTransaction")
		}
	},

	created() {
		this.fetchTransactions()
		this.fetchPayments()
	}
}
</script>
